import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import i18n from './i18n';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingScreen from './container/Loading';
import Header from './container/Header';
import './styles/styles.css';
import './styles/inputs.css';
import { canViewTeamSchedule, storage } from './utils/storage';

// Lazy load your components (MainPage loaded eagerly so "/" works when chunks fail on some devices)
import MainPage from './container/Auth/MainPage';


const SignInPage = lazy(() => import('./container/Auth/SignIn'));
const Dashboard = lazy(() => import('./container/Dashboard'));
const DashboardLayout = lazy(() => import('./container/Dashboard/DashboardLayout'));
const Calendar = lazy(() => import('./container/Schedule'));
const Monthly = lazy(() => import('./container/Monthly'));
const SignUp = lazy(() => import('./container/Auth/SignUp'));
const Weekly = lazy(() => import('./container/Weekly'));
const Profile = lazy(() => import('./container/Profile'));
const _AdminSettings = lazy(() => import('./container/AdminSettings'));
const AIScheduler = lazy(() => import('./container/AIScheduler'));
const Templates = lazy(() => import('./container/Templates'));
const TeamSettingsPage = lazy(() => import('./container/AdminSettings/TeamSettingsPage'));
const ForgottenPassword = lazy(() => import('./container/Auth/ForgotPassword'));
const Salaries = lazy(() => import('./container/Salary'));
const Leaves = lazy(() => import('./container/Leaves'));
const POS = lazy(() => import('./container/POS'));
const SuperAdmin = lazy(() => import('./container/SuperAdmin'));
const ContractReaderPage = lazy(() => import('./container/Dashboard/components/ContractReaderPage'));
const LegalPage = lazy(() => import('./container/Legal/LegalPage'));

const TeamScheduleGuard: React.FC<React.PropsWithChildren> = ({ children }) => (
  canViewTeamSchedule(storage.getUserRolesForSelectedTeam())
    ? <>{children}</>
    : <Navigate to="/dashboard" replace />
);

function AppContent() {
  const { t } = useTranslation();
  const [isNightMode, setIsNightMode] = useState(() => {
    try {
      const storedValue = localStorage.getItem('isNightMode');
      return storedValue !== null ? JSON.parse(storedValue) : true;
    } catch {
      return true;
    }
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine); // Track online status

  const toggleNightMode = () => {
    const newMode = !isNightMode;
    setIsNightMode(newMode);
    try {
      localStorage.setItem('isNightMode', JSON.stringify(newMode));
    } catch {
      // ignore when storage is unavailable
    }
  };

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  /* Sync theme to html so body and global bg use same tokens as dashboard */
  useEffect(() => {
    const theme = isNightMode ? 'dark' : 'light';
    document.documentElement.className = isNightMode ? 'night' : 'day';
    document.documentElement.setAttribute('data-theme', theme);
    // Notify native app of theme via WebView bridge
    console.log('theme:' + theme);
  }, [isNightMode]);

  const location = useLocation();
  const isPOSRoute = location.pathname === '/pos';

  return (
    <>
      {/* Non-blocking — a brief offline blip (flaky Wi-Fi, mobile data) used to
          unmount the whole app behind a "loading" screen and wipe any in-progress
          form input; this keeps everything mounted and just surfaces the state. */}
      {!isOnline && (
        <div className="app-offline-banner" role="alert">
          <WifiOff size={14} aria-hidden /> {t('appOfflineBanner')}
        </div>
      )}
      {!isPOSRoute && <Header toggleNightMode={toggleNightMode} isNightMode={isNightMode} />}
      <ToastContainer />
      <main className="app-main">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgottenPassword />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="schedule" element={<Calendar />} />
            <Route path="team-schedule" element={<TeamScheduleGuard><Monthly /></TeamScheduleGuard>} />
            <Route path="weekly" element={<TeamScheduleGuard><Weekly /></TeamScheduleGuard>} />
            <Route path="team/:teamId" element={<TeamSettingsPage />} />
            <Route path="rates" element={<Salaries />} />
            <Route path="leaves" element={<Leaves />} />
            <Route path="profile" element={<Profile />} />
            <Route path="contracts/:id" element={<ContractReaderPage />} />
            <Route path="legal" element={<LegalPage />} />
            <Route path="ai-schedule" element={<AIScheduler />} />
            <Route path="templates" element={<Templates />} />
            <Route path="super-admin" element={<SuperAdmin />} />
          </Route>
          <Route path="/pos" element={<POS />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </>
  );
}

function App() {
  return (
    <Router>
      <I18nextProvider i18n={i18n}>
        <AppContent />
      </I18nextProvider>
    </Router>
  );
}

export default App;
